<template>
  <div class="roteirizacao-alocacao-dialog">
    <!-- Modo diálogo standalone -->
    <v-dialog
      v-if="!embedded && !publicationMode"
      :value="show"
      max-width="80%"
      @input="onDialogInput"
    >
      <v-card>
        <v-card-title>{{ $t('routePlanning.dialogs.allocatePeopleToLines.title') }}</v-card-title>
        <v-card-text>
          <allocation-editor
            :items="items"
            :loading="loading"
            :headers="allocationHeaders"
            :token="token"
            :bulk="bulk"
            :selection="selection"
            :bulk-line-items="bulkLineItems"
            :bulk-line-items-ida="bulkLineItemsIda"
            :bulk-line-items-volta="bulkLineItemsVolta"
            :bulk-group-items="bulkGroupItems"
            :can-apply-bulk="canApplyBulk"
            :fences-by-line="fencesByLine"
            :destination-fence-id="destinationFenceId"
            :destination-fence-name="destinationFenceName"
            :is-round-trip-direction="isRoundTripDirection"
            :reroute-loading="rerouteLoading"
            :can-save="canSave"
            :publication-layout="true"
            @selection-change="onSelectionChange"
            @bulk-line-change="onBulkLineChange"
            @apply-bulk="applyBulk"
            @update:bulk="bulk = $event"
            @item-line-change="onItemLineChange"
            @item-boarding-change="onItemBoardingChange"
            @item-alighting-change="onItemAlightingChange"
            @cancel="close"
            @save="onSaveClick"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Modo embutido (wizard de publicação) -->
    <div v-else-if="embedded || publicationMode">
      <template v-if="capacityConfirm.show">
        <v-alert type="warning" outlined>
          {{ $t('routePlanning.dialogs.allocatePeopleToLines.warnCapacityMessage', { count: capacityConfirm.count }) }}
        </v-alert>
        <v-radio-group v-model="publicationCapacityChoice" hide-details>
          <v-radio :label="$t('routePlanning.dialogs.allocatePeopleToLines.warnCapacityCancel')" :value="false" />
          <v-radio :label="$t('routePlanning.dialogs.allocatePeopleToLines.warnCapacityContinue')" :value="true" />
        </v-radio-group>
      </template>
      <template v-else-if="newStopConfirm.show">
        <v-alert type="warning" outlined>
          {{ $t('routePlanning.dialogs.allocatePeopleToLines.warnNewStopMessage', { count: newStopConfirm.count }) }}
        </v-alert>
        <v-radio-group v-model="publicationNewStopChoice" hide-details>
          <v-radio :label="$t('routePlanning.dialogs.allocatePeopleToLines.warnNewStopSkip')" :value="false" />
          <v-radio :label="$t('routePlanning.dialogs.allocatePeopleToLines.warnNewStopConfirm')" :value="true" />
        </v-radio-group>
      </template>
      <template v-else>
        <h3 class="text-h6 mb-4">{{ $t('routePlanning.dialogs.allocatePeopleToLines.title') }}</h3>
        <allocation-editor
          :items="items"
          :loading="loading"
          :headers="allocationHeaders"
          :token="token"
          :bulk="bulk"
          :selection="selection"
          :bulk-line-items="bulkLineItems"
          :bulk-line-items-ida="bulkLineItemsIda"
          :bulk-line-items-volta="bulkLineItemsVolta"
          :bulk-group-items="bulkGroupItems"
          :can-apply-bulk="canApplyBulk"
          :fences-by-line="fencesByLine"
          :destination-fence-id="destinationFenceId"
          :destination-fence-name="destinationFenceName"
          :is-round-trip-direction="isRoundTripDirection"
          :reroute-loading="rerouteLoading"
          :can-save="canSave"
          :hide-actions="true"
          :publication-layout="publicationMode"
          @selection-change="onSelectionChange"
          @bulk-line-change="onBulkLineChange"
          @apply-bulk="applyBulk"
          @update:bulk="bulk = $event"
          @item-line-change="onItemLineChange"
          @item-boarding-change="onItemBoardingChange"
          @item-alighting-change="onItemAlightingChange"
        />
      </template>
    </div>

    <!-- Confirmações standalone (fora do wizard) -->
    <template v-if="!publicationMode">
      <v-dialog v-model="rejectedConfirm.show" max-width="500" persistent>
        <v-card>
          <v-card-title>{{ $t('routePlanning.dialogs.allocatePeopleToLines.warnRejectedTitle') }}</v-card-title>
          <v-card-text>
            {{ $t('routePlanning.dialogs.allocatePeopleToLines.warnRejectedMessage', { count: rejectedConfirm.count }) }}
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn color="primary" text @click="cancelRejectedConfirm">{{ $t('button.cancel') }}</v-btn>
            <v-btn color="primary" dark @click="confirmRejected">
              {{ $t('routePlanning.dialogs.allocatePeopleToLines.warnRejectedContinue') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="newStopConfirm.show" max-width="560" persistent>
        <v-card>
          <v-card-title>{{ $t('routePlanning.dialogs.allocatePeopleToLines.warnNewStopTitle') }}</v-card-title>
          <v-card-text>
            {{ $t('routePlanning.dialogs.allocatePeopleToLines.warnNewStopMessage', { count: newStopConfirm.count }) }}
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn color="primary" text @click="resolveNewStopConfirm(false)">
              {{ $t('routePlanning.dialogs.allocatePeopleToLines.warnNewStopSkip') }}
            </v-btn>
            <v-btn color="primary" dark @click="resolveNewStopConfirm(true)">
              {{ $t('routePlanning.dialogs.allocatePeopleToLines.warnNewStopConfirm') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="capacityConfirm.show" max-width="560" persistent>
        <v-card>
          <v-card-title>{{ $t('routePlanning.dialogs.allocatePeopleToLines.warnCapacityTitle') }}</v-card-title>
          <v-card-text>
            {{ $t('routePlanning.dialogs.allocatePeopleToLines.warnCapacityMessage', { count: capacityConfirm.count }) }}
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn color="primary" text @click="resolveCapacityConfirm(false)">
              {{ $t('routePlanning.dialogs.allocatePeopleToLines.warnCapacityCancel') }}
            </v-btn>
            <v-btn color="primary" dark @click="resolveCapacityConfirm(true)">
              {{ $t('routePlanning.dialogs.allocatePeopleToLines.warnCapacityContinue') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </div>
</template>

<script>
import apiController from '@/utils/apiController';
import EventBus from '@/utils/eventBus';
import {
  createFencesForAllocationStops,
  VALIDO_CRIACAO_PONTO,
} from '@/utils/allocationFenceCreation';
import RoteirizacaoAlocacaoEditor from './RoteirizacaoAlocacaoEditor.vue';

function passengerHasNoUnit(p) {
  if (!p) return true;
  const id = p.id_unidade;
  return id == null || id === '' || Number(id) === -1;
}

export default {
  name: 'RoteirizacaoAlocacaoDialog',
  components: { AllocationEditor: RoteirizacaoAlocacaoEditor },
  props: {
    value: { type: Boolean, default: false },
    embedded: { type: Boolean, default: false },
    publicationMode: { type: Boolean, default: false },
    token: { type: String, default: '' },
    validatedAddresses: { type: Array, default: () => [] },
    passengers: { type: Array, default: () => [] },
    groups: { type: Array, default: () => [] },
    shifts: { type: Array, default: () => [] },
    passengerPositions: { type: Object, default: () => ({}) },
    destinationFenceId: { type: [Number, String], default: null },
    destinationFenceName: { type: String, default: '' },
    isRoundTripDirection: { type: Boolean, default: false },
    dateRangeValid: { type: Boolean, default: true },
    bypassDateRange: { type: Boolean, default: false },
    serviceTime: { type: Number, default: 0 },
    maxNumberofStops: { type: Number, default: null },
    lineCapacityFallback: { type: Function, default: null },
    pollProcess: { type: Function, default: null },
    resolvePassengerCoords: { type: Function, default: null },
    resolvePassengerAddress: { type: Function, default: null },
    isRowRejected: { type: Function, default: null },
    historicoSessaoId: { type: String, default: null },
    historicoSessaoOrigemId: { type: String, default: null },
    historicoOrigem: { type: String, default: "ROTEIRIZACAO" },
  },
  data() {
    return {
      show: false,
      loading: false,
      items: [],
      selection: [],
      bulk: { id_grupo: null, id_linha_ida: null, id_linha_volta: null },
      fencesByLine: {},
      rerouteLoading: false,
      rejectedConfirm: { show: false, selection: [], count: 0 },
      newStopConfirm: { show: false, items: [], count: 0, resolve: null },
      capacityConfirm: { show: false, items: [], count: 0, resolve: null },
      publicationCapacityChoice: null,
      publicationNewStopChoice: null,
    };
  },
  computed: {
    allocationHeaders() {
      return [
        { text: this.$t('routePlanning.fields.passengerRegistration'), value: 'matricula', width: 105 },
        { text: this.$t('routePlanning.fields.passengerName'), value: 'nome', width: 150 },
        { text: this.$t('validationSituation.headers.situation'), value: 'nome_situacao_validacao', width: 90 },
        { text: this.$t('candidate.headers.main.unit'), value: 'cod_unidade', width: 105 },
        { text: this.$t('routePlanning.fields.pcd'), value: 'pcd_extenso', width: 80 },
        { text: this.$t('routePlanning.fields.nearestLineDistance'), value: 'distancia_linha_mais_proxima_extenso', width: 110 },
        { text: this.$t('routePlanning.fields.nearestLineName'), value: 'linha_mais_proxima_extenso', width: 180 },
        { text: this.$t('routePlanning.fields.selectGroup'), value: 'id_grupo_alocado', width: 125 },
        {
          text: this.$t('guiasMotorista.directionIda'),
          value: 'trajeto_ida',
          width: 285,
        },
        {
          text: this.$t('guiasMotorista.directionVolta'),
          value: 'trajeto_volta',
          width: 285,
        },
      ];
    },
    bulkLineItemsIda() {
      return this.intersectionLineItems('ida');
    },
    bulkLineItemsVolta() {
      return this.intersectionLineItems('volta');
    },
    bulkLineItems() {
      const byId = new Map();
      [...this.bulkLineItemsIda, ...this.bulkLineItemsVolta].forEach((line) => {
        if (line && line.id_linha != null) byId.set(String(line.id_linha), line);
      });
      return [...byId.values()];
    },
    bulkGroupItems() {
      const sel = this.selection || [];
      if (!sel.length) return [];
      let ids = null;
      const byId = new Map();
      const lineFilters = [this.bulk.id_linha_ida, this.bulk.id_linha_volta]
        .filter((id) => id != null && id !== '')
        .map((id) => String(id));
      for (const item of sel) {
        let grupos = Array.isArray(item?.grupos_linha) ? item.grupos_linha : [];
        if (lineFilters.length) {
          grupos = grupos.filter((g) => lineFilters.includes(String(g?.id_linha)));
        }
        const itemIds = new Set(
          grupos.filter((g) => g?.id_grupo != null).map((g) => String(g.id_grupo))
        );
        grupos.forEach((g) => {
          if (g?.id_grupo != null && !byId.has(String(g.id_grupo))) {
            byId.set(String(g.id_grupo), g);
          }
        });
        ids = ids == null ? itemIds : new Set([...ids].filter((id) => itemIds.has(id)));
      }
      if (!ids || !ids.size) return [];
      return [...ids].map((id) => byId.get(id)).filter(Boolean);
    },
    canApplyBulk() {
      return !!(this.selection || []).length
        && (this.bulk.id_grupo != null
          || this.bulk.id_linha_ida != null
          || this.bulk.id_linha_volta != null);
    },
    canSave() {
      return this.bypassDateRange || this.dateRangeValid;
    },
    confirmingCapacity() {
      return !!this.capacityConfirm.show;
    },
    confirmingNewStop() {
      return !!this.newStopConfirm.show;
    },
  },
  watch: {
    value(v) {
      if (!this.embedded && !this.publicationMode) this.show = !!v;
    },
    show(v) {
      if (!this.embedded && !this.publicationMode) this.$emit('input', v);
    },
  },
  methods: {
    /** Campos da linha mais próxima (rejeitados) com fallback '-'. */
    nearestLineFieldsFrom(origem) {
      const dash = (v) => (v != null && v !== '' ? v : '-');
      return {
        distancia_linha_mais_proxima: origem?.distancia_linha_mais_proxima ?? null,
        distancia_linha_mais_proxima_extenso: dash(origem?.distancia_linha_mais_proxima_extenso),
        cod_linha_mais_proxima: dash(origem?.cod_linha_mais_proxima),
        nome_linha_mais_proxima: dash(origem?.nome_linha_mais_proxima),
        linha_mais_proxima_extenso: dash(origem?.linha_mais_proxima_extenso),
        id_linha_mais_proxima: origem?.id_linha_mais_proxima ?? null,
      };
    },
    intersectionLineItems(direction) {
      const sel = this.selection || [];
      if (!sel.length) return [];
      let ids = null;
      const byId = new Map();
      for (const item of sel) {
        const linhas = (Array.isArray(item?.linhas_validadas) ? item.linhas_validadas : [])
          .filter((line) => this.lineSupportsSense(line, direction));
        const itemIds = new Set(
          linhas.filter((l) => l?.id_linha != null).map((l) => String(l.id_linha))
        );
        linhas.forEach((l) => {
          if (l?.id_linha != null && !byId.has(String(l.id_linha))) {
            byId.set(String(l.id_linha), l);
          }
        });
        ids = ids == null ? itemIds : new Set([...ids].filter((id) => itemIds.has(id)));
      }
      if (!ids || !ids.size) return [];
      return [...ids].map((id) => byId.get(id)).filter(Boolean);
    },
    lineSense(line) {
      const raw = line && (line.sentido != null ? line.sentido : line.id_sentido);
      const value = Number(raw);
      return value === 1 || value === 2 ? value : null;
    },
    lineSupportsSense(line, direction) {
      const sense = this.lineSense(line);
      if (sense == null) return true;
      return direction === 'ida' ? sense === 1 : sense === 2;
    },
    normalizeLineOption(line) {
      if (!line || line.id_linha == null) return null;
      const sense = this.lineSense(line);
      return {
        ...line,
        id_linha: line.id_linha,
        cod_linha: line.cod_linha || line.nome_linha || String(line.id_linha),
        nome_linha: line.nome_linha || line.cod_linha || String(line.id_linha),
        ...(sense != null ? { sentido: sense, id_sentido: sense } : {}),
      };
    },
    selectedLineIds(item) {
      if (!item) return [];
      const explicit = [
        item.id_linha_ida_alocada,
        item.id_linha_volta_alocada,
      ].filter((id) => id != null && id !== '');
      const ids = explicit.length ? explicit : [item.id_linha_alocada];
      return [...new Set(ids.filter((id) => id != null && id !== '').map((id) => String(id)))];
    },
    referenceLineId(item) {
      if (!item) return null;
      return item.id_linha_ida_alocada != null
        ? item.id_linha_ida_alocada
        : (item.id_linha_volta_alocada != null ? item.id_linha_volta_alocada : item.id_linha_alocada);
    },
    linesForItem(item, direction) {
      const lines = Array.isArray(item?.linhas_validadas) ? item.linhas_validadas : [];
      return lines.filter((line) => this.lineSupportsSense(line, direction));
    },
    onDialogInput(v) {
      if (!v) this.close();
      else this.show = true;
    },
    /**
     * Abre o diálogo com a seleção. Pode perguntar sobre rejeitados antes.
     */
    async open(sel, { skipRejectedConfirm = false } = {}) {
      if (!sel || !sel.length) return;
      if (!skipRejectedConfirm) {
        const rejectedCount = this.countRejected(sel);
        if (rejectedCount > 0) {
          this.rejectedConfirm = {
            show: true,
            selection: Array.isArray(sel) ? [...sel] : [],
            count: rejectedCount,
          };
          return;
        }
      }
      await this.populateAndShow(sel);
    },
    countRejected(sel) {
      if (typeof this.isRowRejected === 'function') {
        return (sel || []).filter((row) => this.isRowRejected(row)).length;
      }
      return 0;
    },
    confirmRejected() {
      const sel = this.rejectedConfirm.selection || [];
      this.rejectedConfirm = { show: false, selection: [], count: 0 };
      if (!sel.length) {
        this.$emit('rejected-cancelled');
        return;
      }
      this.open(sel, { skipRejectedConfirm: true });
    },
    cancelRejectedConfirm() {
      this.rejectedConfirm = { show: false, selection: [], count: 0 };
      this.$emit('rejected-cancelled');
    },
    async populateAndShow(sel) {
      this.loading = true;
      this.selection = [];
      this.bulk = { id_grupo: null, id_linha_ida: null, id_linha_volta: null };
      this.publicationCapacityChoice = null;
      this.publicationNewStopChoice = null;

      const linhasIdsParaFetch = [];
      const draftItems = (sel || []).map((e) => {
        const origem = (this.validatedAddresses || []).find(
          (x) => String(x?.id_pessoa) === String(e?.id_pessoa)
        ) || e;
        const linhasValidadasOrigem = Array.isArray(origem?.linhas_validadas)
          ? origem.linhas_validadas
          : [];
        const linhasValidadasDerivadas = Object.keys(origem || {})
          .filter((k) => k.startsWith('id_linha_') && origem[k] != null)
          .map((k) => {
            const sufixo = k.slice('id_linha_'.length);
            return {
              id_linha: origem[k],
              cod_linha: origem[`cod_linha_${sufixo}`] != null
                ? origem[`cod_linha_${sufixo}`]
                : origem[k],
              sentido: sufixo === 'ida' ? 1 : (sufixo === 'volta' ? 2 : null),
            };
          });
        const linhasValidadasBase = linhasValidadasOrigem.length > 0
          ? linhasValidadasOrigem
          : linhasValidadasDerivadas;
        const linhasById = new Map();
        linhasValidadasBase
          .map((line) => this.normalizeLineOption(line))
          .filter(Boolean)
          .forEach((line) => {
            const key = String(line.id_linha);
            const previous = linhasById.get(key);
            if (!previous) {
              linhasById.set(key, line);
              return;
            }
            const prevSense = this.lineSense(previous);
            const currentSense = this.lineSense(line);
            if (prevSense != null && currentSense != null && prevSense !== currentSense) {
              linhasById.set(key, { ...previous, sentido: null, id_sentido: null });
            }
          });
        const linhasValidadas = [...linhasById.values()];
        linhasValidadas.forEach((l) => linhasIdsParaFetch.push(l.id_linha));

        const gruposPorLinhaGlobal = (this.groups || []).filter((g) =>
          linhasValidadas.some((l) => String(l.id_linha) === String(g.id_linha))
        );
        const gruposOrigem = Array.isArray(origem?.grupos_linha) ? origem.grupos_linha : [];
        const gruposLinhaBruto = gruposOrigem.length > 0 ? gruposOrigem : gruposPorLinhaGlobal;
        const gruposLinha = gruposLinhaBruto.map((g) => ({
          ...g,
          id_grupo: g?.id_grupo ?? null,
          nome: g?.nome ?? g?.nome_grupo ?? g?.cod_grupo
            ?? (g?.id_grupo != null ? `Grupo ${g.id_grupo}` : '-'),
        }));
        const passageiro = (this.passengers || []).find(
          (x) => String(x?.id_pessoa) === String(origem?.id_pessoa)
        );
        const currentIda = origem?.id_linha_ida !== undefined
          ? origem.id_linha_ida
          : (passageiro?.id_linha_ida != null ? passageiro.id_linha_ida : null);
        const currentVolta = origem?.id_linha_volta !== undefined
          ? origem.id_linha_volta
          : (passageiro?.id_linha_volta != null ? passageiro.id_linha_volta : null);
        const firstForDirection = (direction) => {
          const matching = linhasValidadas.find((line) => this.lineSupportsSense(line, direction));
          if (matching) return matching;
          const hasKnownSense = linhasValidadas.some((line) => this.lineSense(line) != null);
          return hasKnownSense ? null : (linhasValidadas[0] || null);
        };
        const lineForId = (id) => id == null
          ? null
          : linhasValidadas.find((line) => String(line.id_linha) === String(id));
        const idLinhaIda = lineForId(currentIda)
          ? currentIda
          : (firstForDirection('ida')?.id_linha ?? null);
        const idLinhaVolta = lineForId(currentVolta)
          ? currentVolta
          : (firstForDirection('volta')?.id_linha ?? null);
        const idLinhaAlocada = idLinhaIda != null ? idLinhaIda : idLinhaVolta;
        const idGrupoAlocado = idLinhaAlocada != null
          ? gruposLinha.find((g) => String(g.id_linha) === String(idLinhaAlocada))?.id_grupo ?? null
          : null;
        const idSit = origem?.id_situacao_validacao != null
          ? Number(origem.id_situacao_validacao)
          : (passageiro != null ? Number(passageiro.id_situacao_validacao) : null);
        const pcd = origem?.pcd != null
          ? (Number(origem.pcd) === 1 ? 1 : 0)
          : (passageiro && (passageiro.pcd === 1 || passageiro.pcd === true) ? 1 : 0);

        let lat = origem?.lat != null ? Number(origem.lat) : null;
        let lng = origem?.lng != null ? Number(origem.lng) : null;
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          if (typeof this.resolvePassengerCoords === 'function') {
            const coords = this.resolvePassengerCoords(passageiro || origem, origem);
            lat = coords.lat;
            lng = coords.lng;
          } else {
            const fromPos = origem?.id_pessoa != null
              ? (this.passengerPositions || {})[String(origem.id_pessoa)]
              : null;
            if (fromPos && Number.isFinite(Number(fromPos.lat)) && Number.isFinite(Number(fromPos.lng))) {
              lat = Number(fromPos.lat);
              lng = Number(fromPos.lng);
            } else {
              lat = null;
              lng = null;
            }
          }
        }

        const endereco = typeof this.resolvePassengerAddress === 'function'
          ? (this.resolvePassengerAddress(passageiro || origem) || '')
          : String(origem?.endereco || '').trim();

        return {
          id_pessoa: origem?.id_pessoa,
          matricula: origem?.matricula,
          nome: origem?.nome,
          id_situacao_validacao: idSit,
          nome_situacao_validacao: origem?.nome_situacao_validacao != null && origem?.nome_situacao_validacao !== ''
            ? origem.nome_situacao_validacao
            : '-',
          cod_unidade: origem?.cod_unidade != null && origem?.cod_unidade !== ''
            ? origem.cod_unidade
            : '-',
          id_unidade: origem?.id_unidade ?? (passageiro && passageiro.id_unidade),
          id_grupo_alocado: idGrupoAlocado,
          id_linha_alocada: idLinhaAlocada,
          id_linha_ida_alocada: idLinhaIda,
          id_linha_volta_alocada: idLinhaVolta,
          id_cerca_embarque: origem?.id_cerca_embarque ?? null,
          id_cerca_desembarque: origem?.id_cerca_desembarque ?? null,
          id_cerca_desembarque_ida: idLinhaIda != null ? this.destinationFenceId : null,
          id_cerca_embarque_volta: idLinhaVolta != null ? this.destinationFenceId : null,
          grupos_linha: gruposLinha,
          linhas_validadas: linhasValidadas,
          melhor_cerca_por_linha: origem?.melhor_cerca_por_linha || {},
          valido_por_linha: origem?.valido_por_linha || {},
          position_por_linha: origem?.position_por_linha || {},
          pcd,
          pcd_extenso: Number(pcd) === 1
            ? this.$t('selectOptions.yes')
            : this.$t('selectOptions.no'),
          ...this.nearestLineFieldsFrom(origem),
          endereco,
          lat,
          lng,
        };
      });

      await this.ensureFencesForLines(linhasIdsParaFetch);
      this.items = draftItems.map((item) => {
        this.applyFencePrefill(item, item);
        return item;
      });
      this.loading = false;
      this.show = true;
      this.$emit('populated');
    },
    close({ emitContinue = true } = {}) {
      this.show = false;
      this.items = [];
      this.loading = false;
      this.selection = [];
      this.bulk = { id_grupo: null, id_linha_ida: null, id_linha_volta: null };
      this.fencesByLine = {};
      this.newStopConfirm = { show: false, items: [], count: 0, resolve: null };
      this.capacityConfirm = { show: false, items: [], count: 0, resolve: null };
      this.rerouteLoading = false;
      this.publicationCapacityChoice = null;
      this.publicationNewStopChoice = null;
      this.$emit('input', false);
      this.$emit('closed', { emitContinue });
    },
    async ensureFencesForLines(idLinhas) {
      const ids = [...new Set((idLinhas || []).filter((id) => id != null && id !== ''))];
      const missing = ids.filter((id) => !this.fencesByLine[String(id)]);
      if (!missing.length) return;
      try {
        const resp = await apiController.request(apiController.endpoints.getCercasporlinha, {
          data: missing,
          silent: true,
        });
        const rows = Array.isArray(resp.data) ? resp.data : [];
        const byLine = {};
        for (const row of rows) {
          if (!row || row.id_linha == null || row.id_cerca == null) continue;
          const tipo = Number(row.id_tipo_cerca);
          if (tipo === 1 || tipo === 4) continue;
          const key = String(row.id_linha);
          if (!byLine[key]) byLine[key] = [];
          const existing = byLine[key].find((c) => String(c.id_cerca) === String(row.id_cerca));
          const rowSentido = row.id_sentido != null ? Number(row.id_sentido) : null;
          if (existing) {
            const prev = Number(existing.id_sentido);
            if (Number.isFinite(prev) && Number.isFinite(rowSentido) && prev !== rowSentido) {
              existing.id_sentido = -1;
            }
            continue;
          }
          byLine[key].push({
            id_cerca: row.id_cerca,
            nome_cerca: row.nome_cerca || row.endereco || row.cod_cerca || String(row.id_cerca),
            endereco: row.endereco,
            cod_cerca: row.cod_cerca,
            id_tipo_cerca: row.id_tipo_cerca,
            id_sentido: Number.isFinite(rowSentido) ? rowSentido : null,
            ordem_parada: row.ordem_parada,
            lat_centro: row.lat_centro != null ? Number(row.lat_centro) : null,
            lng_centro: row.lng_centro != null ? Number(row.lng_centro) : null,
          });
        }
        const next = { ...this.fencesByLine };
        for (const id of missing) {
          next[String(id)] = byLine[String(id)] || [];
        }
        this.fencesByLine = next;
      } catch (e) {
        EventBus.$emit('show-error', { error: e });
      }
    },
    fencesForLine(idLinha) {
      if (idLinha == null || idLinha === '') return [];
      return this.fencesByLine[String(idLinha)] || [];
    },
    isRoundTrip(idLinha) {
      const fences = this.fencesForLine(idLinha);
      let hasIda = false;
      let hasVolta = false;
      for (const f of fences) {
        const s = Number(f && f.id_sentido);
        if (!Number.isFinite(s)) continue;
        if (s === -1) return true;
        if (s === 1 || s === 3 || s === 4) hasIda = true;
        if (s === 2 || s === 5 || s === 6) hasVolta = true;
        if (hasIda && hasVolta) return true;
      }
      return this.isRoundTripDirection;
    },
    fenceSupportsDirection(fence, direction) {
      const sense = Number(fence && fence.id_sentido);
      if (!Number.isFinite(sense) || sense === -1) return true;
      return direction === 'volta'
        ? sense === 2 || sense === 5 || sense === 6
        : sense === 1 || sense === 3 || sense === 4;
    },
    selectableFencesForLine(idLinha, direction) {
      const destinationId = this.destinationFenceId != null
        ? String(this.destinationFenceId)
        : null;
      const useBothDirections = this.isRoundTrip(idLinha);
      return this.fencesForLine(idLinha).filter((fence) => (
        (!destinationId || String(fence.id_cerca) !== destinationId)
        && (useBothDirections || this.fenceSupportsDirection(fence, direction))
      ));
    },
    resolveBestFence(origem, idLinha, direction = 'ida') {
      if (!origem || idLinha == null || idLinha === '') return null;
      const key = String(idLinha);
      const fromMap = origem.melhor_cerca_por_linha && origem.melhor_cerca_por_linha[key];
      const storedField = direction === 'volta' ? 'id_cerca_desembarque' : 'id_cerca_embarque';
      let idCerca = origem[storedField] != null
        ? origem[storedField]
        : (fromMap && fromMap.id_cerca != null ? fromMap.id_cerca : null);
      if (idCerca == null) {
        for (const t of this.shifts || []) {
          const desc = t.descricao_turno;
          if (String(origem[`id_linha_${desc}`]) !== key) continue;
          if (origem[`id_cerca_${desc}`] != null) {
            idCerca = origem[`id_cerca_${desc}`];
            break;
          }
        }
      }
      const selectable = this.selectableFencesForLine(idLinha, direction);
      if (idCerca == null) return null;
      const onLine = selectable
        .some((c) => String(c.id_cerca) === String(idCerca));
      return onLine ? idCerca : null;
    },
    applyFencePrefill(item, origem) {
      if (!item) return;
      this.applyFencePrefillForDirection(item, origem, 'ida');
      this.applyFencePrefillForDirection(item, origem, 'volta');
      this.syncTripStops(item);
    },
    applyFencePrefillForDirection(item, origem, direction) {
      if (!item) return;
      const isVolta = direction === 'volta';
      const lineField = isVolta ? 'id_linha_volta_alocada' : 'id_linha_ida_alocada';
      const fenceField = isVolta ? 'id_cerca_desembarque' : 'id_cerca_embarque';
      const idLinha = item[lineField];
      item[fenceField] = idLinha != null && idLinha !== ''
        ? this.resolveBestFence(origem || item, idLinha, direction)
        : null;
    },
    onItemBoardingChange(item) {
      if (!item) return;
      this.syncTripStops(item);
    },
    onItemAlightingChange(item) {
      if (!item) return;
      this.syncTripStops(item);
    },
    syncTripStops(item) {
      if (!item) return;
      const hasIda = item.id_linha_ida_alocada != null && item.id_linha_ida_alocada !== '';
      const hasVolta = item.id_linha_volta_alocada != null && item.id_linha_volta_alocada !== '';
      item.id_cerca_desembarque_ida = hasIda ? this.destinationFenceId : null;
      item.id_cerca_embarque_volta = hasVolta ? this.destinationFenceId : null;
      if (!hasIda) item.id_cerca_embarque = null;
      if (!hasVolta) item.id_cerca_desembarque = null;
    },
    onItemLineChange(item, direction) {
      if (!item) return;
      const origem = (this.validatedAddresses || []).find(
        (x) => String(x?.id_pessoa) === String(item.id_pessoa)
      ) || item;
      const selectedLines = this.selectedLineIds(item);
      item.id_linha_alocada = item.id_linha_ida_alocada != null
        ? item.id_linha_ida_alocada
        : item.id_linha_volta_alocada;
      if (selectedLines.length) {
        const grupoOk = (item.grupos_linha || []).some(
          (g) => String(g?.id_grupo) === String(item.id_grupo_alocado)
            && selectedLines.includes(String(g?.id_linha))
        );
        if (!grupoOk) {
          item.id_grupo_alocado = (item.grupos_linha || []).find(
            (g) => selectedLines.includes(String(g?.id_linha))
          )?.id_grupo ?? null;
        }
      } else {
        item.id_grupo_alocado = null;
      }
      if (direction === 'ida' || direction === 'both') {
        this.applyFencePrefillForDirection(item, origem, 'ida');
      }
      if (direction === 'volta' || direction === 'both') {
        this.applyFencePrefillForDirection(item, origem, 'volta');
      }
      this.syncTripStops(item);
    },
    onSelectionChange(items) {
      this.selection = Array.isArray(items) ? items : [];
      if (!this.selection.length) {
        this.bulk = { id_grupo: null, id_linha_ida: null, id_linha_volta: null };
        return;
      }
      const idaOk = this.bulk.id_linha_ida == null
        || this.bulkLineItemsIda.some((l) => String(l.id_linha) === String(this.bulk.id_linha_ida));
      if (!idaOk) this.bulk.id_linha_ida = null;
      const voltaOk = this.bulk.id_linha_volta == null
        || this.bulkLineItemsVolta.some((l) => String(l.id_linha) === String(this.bulk.id_linha_volta));
      if (!voltaOk) this.bulk.id_linha_volta = null;
      const groupOk = this.bulk.id_grupo == null
        || this.bulkGroupItems.some((g) => String(g.id_grupo) === String(this.bulk.id_grupo));
      if (!groupOk) this.bulk.id_grupo = null;
    },
    onBulkLineChange() {
      const groupOk = this.bulk.id_grupo == null
        || this.bulkGroupItems.some((g) => String(g.id_grupo) === String(this.bulk.id_grupo));
      if (!groupOk) this.bulk.id_grupo = null;
    },
    applyBulk() {
      if (!this.canApplyBulk) return;
      const idLinhaIda = this.bulk.id_linha_ida;
      const idLinhaVolta = this.bulk.id_linha_volta;
      const idGrupo = this.bulk.id_grupo;
      for (const item of this.selection) {
        if (!item) continue;
        let changed = false;
        let idaChanged = false;
        let voltaChanged = false;
        if (idLinhaIda != null && this.linesForItem(item, 'ida').some(
          (line) => String(line.id_linha) === String(idLinhaIda)
        )) {
          idaChanged = String(item.id_linha_ida_alocada) !== String(idLinhaIda);
          changed = changed || idaChanged;
          item.id_linha_ida_alocada = idLinhaIda;
        }
        if (idLinhaVolta != null && this.linesForItem(item, 'volta').some(
          (line) => String(line.id_linha) === String(idLinhaVolta)
        )) {
          voltaChanged = String(item.id_linha_volta_alocada) !== String(idLinhaVolta);
          changed = changed || voltaChanged;
          item.id_linha_volta_alocada = idLinhaVolta;
        }
        item.id_linha_alocada = item.id_linha_ida_alocada != null
          ? item.id_linha_ida_alocada
          : item.id_linha_volta_alocada;
        if (idGrupo == null && changed) {
          const selectedLines = this.selectedLineIds(item);
          const grupoAtualOk = (item.grupos_linha || []).some(
            (g) => String(g?.id_grupo) === String(item.id_grupo_alocado)
              && selectedLines.includes(String(g?.id_linha))
          );
          if (!grupoAtualOk) {
            item.id_grupo_alocado = (item.grupos_linha || []).find(
              (g) => selectedLines.includes(String(g?.id_linha))
            )?.id_grupo ?? null;
          }
        }
        if (idGrupo != null) {
          const hasGroup = (item.grupos_linha || []).some(
            (g) => String(g?.id_grupo) === String(idGrupo)
          );
          if (hasGroup) item.id_grupo_alocado = idGrupo;
        }
        if (idaChanged || item.id_cerca_embarque == null || item.id_cerca_embarque === '') {
          this.applyFencePrefillForDirection(item, item, 'ida');
        }
        if (voltaChanged || item.id_cerca_desembarque == null || item.id_cerca_desembarque === '') {
          this.applyFencePrefillForDirection(item, item, 'volta');
        }
        if (changed) this.syncTripStops(item);
      }
    },
    allocationStopTargets(item) {
      if (!item || item.id_pessoa == null) return [];
      const isPcd = Number(item.pcd) === 1 || item.pcd === true;
      const targets = [];
      const addIfNeeded = (direction, idLinha, field) => {
        if (idLinha == null || idLinha === '') return;
        const missing = item[field] == null || item[field] === '';
        const requiresCreation = Number(
          item.valido_por_linha && item.valido_por_linha[String(idLinha)]
        ) === VALIDO_CRIACAO_PONTO;
        if (isPcd || (missing && requiresCreation)) {
          targets.push({ direction, idLinha, field });
        }
      };
      addIfNeeded('ida', item.id_linha_ida_alocada, 'id_cerca_embarque');
      addIfNeeded('volta', item.id_linha_volta_alocada, 'id_cerca_desembarque');
      return targets;
    },
    itemsNeedingNewStop(sel) {
      return (sel || []).filter((item) => this.allocationStopTargets(item).length > 0);
    },
    async fetchLineCapacity(idLinha) {
      if (idLinha == null || idLinha === '') return null;
      try {
        const res = await apiController.request(apiController.endpoints.getTurnospassageiroporlinha, {
          params: { idlinha: idLinha },
          silent: true,
        });
        const rows = Array.isArray(res.data) ? res.data : [];
        const caps = rows
          .map((r) => Number(r && r.lotacao))
          .filter((n) => Number.isFinite(n) && n > 0);
        if (caps.length) return Math.min(...caps);
      } catch (_) { /* fallback */ }
      if (typeof this.lineCapacityFallback === 'function') {
        return this.lineCapacityFallback(idLinha);
      }
      return null;
    },
    async fetchPassengersAlreadyOnLines(lineIds) {
      const out = [];
      const seen = new Set();
      for (const idLinha of lineIds || []) {
        if (idLinha == null || idLinha === '') continue;
        try {
          const res = await apiController.request(apiController.endpoints.getPessoasporlinha, {
            params: { idlinha: idLinha },
            silent: true,
          });
          const rows = Array.isArray(res.data) ? res.data : [];
          for (const p of rows) {
            if (!p || p.id_pessoa == null) continue;
            const id = String(p.id_pessoa);
            if (seen.has(id)) continue;
            seen.add(id);
            out.push(p);
          }
        } catch (_) { /* tolerado */ }
      }
      return out;
    },
    async findOverCapacity(sel) {
      const items = (sel || []).filter(
        (item) => item && item.id_pessoa != null && this.selectedLineIds(item).length
      );
      if (!items.length) return [];
      const flagged = new Map();
      const mark = (item) => {
        const id = String(item.id_pessoa);
        if (!flagged.has(id)) flagged.set(id, item);
      };
      for (const item of items) {
        this.selectedLineIds(item).forEach((key) => {
          const v = item.valido_por_linha && item.valido_por_linha[key];
          if (Number(v) === 2) mark(item);
        });
      }
      const byLine = new Map();
      for (const item of items) {
        this.selectedLineIds(item).forEach((key) => {
          if (!byLine.has(key)) byLine.set(key, []);
          byLine.get(key).push(item);
        });
      }
      await Promise.all([...byLine.entries()].map(async ([idLinhaStr, lineItems]) => {
        const capacity = await this.fetchLineCapacity(idLinhaStr);
        if (!(Number.isFinite(capacity) && capacity > 0)) return;
        const batchIds = new Set(lineItems.map((i) => String(i.id_pessoa)));
        let occupiedOutsideBatch = 0;
        try {
          const already = await this.fetchPassengersAlreadyOnLines([idLinhaStr]);
          occupiedOutsideBatch = (already || []).filter(
            (p) => p && p.id_pessoa != null && !batchIds.has(String(p.id_pessoa))
          ).length;
        } catch (_) {
          occupiedOutsideBatch = 0;
        }
        if (occupiedOutsideBatch + lineItems.length > capacity) {
          lineItems.forEach(mark);
        }
      }));
      return [...flagged.values()];
    },
    resolveNewStopConfirm(accepted) {
      const resolve = this.newStopConfirm.resolve;
      this.newStopConfirm = { show: false, items: [], count: 0, resolve: null };
      if (typeof resolve === 'function') resolve(!!accepted);
    },
    resolveCapacityConfirm(accepted) {
      const resolve = this.capacityConfirm.resolve;
      this.capacityConfirm = { show: false, items: [], count: 0, resolve: null };
      if (typeof resolve === 'function') resolve(!!accepted);
    },
    /** Avanço do wizard: resolve confirms ou dispara o save. */
    async advanceFromPublication() {
      if (this.capacityConfirm.show) {
        const accepted = this.publicationCapacityChoice;
        this.publicationCapacityChoice = null;
        this.resolveCapacityConfirm(accepted);
        return;
      }
      if (this.newStopConfirm.show) {
        const accepted = this.publicationNewStopChoice;
        this.publicationNewStopChoice = null;
        this.resolveNewStopConfirm(accepted);
        return;
      }
      await this.onSaveClick();
    },
    async onSaveClick() {
      if (!this.canSave || this.rerouteLoading) return;
      const items = this.items || [];
      const overCapacity = await this.findOverCapacity(items);
      if (overCapacity.length) {
        const continueAllocation = await new Promise((resolve) => {
          this.capacityConfirm = {
            show: true,
            items: overCapacity,
            count: overCapacity.length,
            resolve,
          };
          this.publicationCapacityChoice = null;
        });
        if (!continueAllocation) return;
      }
      const needing = this.itemsNeedingNewStop(items);
      if (!needing.length) {
        await this.persistAllocation(items);
        return;
      }
      const accepted = await new Promise((resolve) => {
        this.newStopConfirm = {
          show: true,
          items: needing,
          count: needing.length,
          resolve,
        };
        this.publicationNewStopChoice = null;
      });
      if (accepted) {
        await this.allocateWithNewStops(items, needing);
      } else {
        await this.persistAllocation(items);
      }
    },
    async allocateWithNewStops(allItems, needingItems) {
      this.rerouteLoading = true;
      if (!this.publicationMode) {
        EventBus.$emit('show-alert', {
          tipo: 'info',
          mensagem: this.$t('routePlanning.dialogs.allocatePeopleToLines.rerouteProgress'),
        });
      }
      try {
        const byLine = new Map();
        const pairs = [];
        for (const item of needingItems || []) {
          this.allocationStopTargets(item).forEach((target) => {
            pairs.push({
              item,
              idLinha: target.idLinha,
              field: target.field,
              priority: target.direction === 'ida' ? 0 : 1,
            });
          });
        }
        pairs.sort((a, b) => a.priority - b.priority);
        for (const { item, idLinha, field } of pairs) {
          const key = String(idLinha);
          if (!byLine.has(key)) byLine.set(key, []);
          const existing = byLine.get(key).find(
            (entry) => String(entry.item.id_pessoa) === String(item.id_pessoa)
          );
          if (existing) {
            existing.fields.add(field);
          } else {
            byLine.get(key).push({ item, fields: new Set([field]) });
          }
        }
        const replacedLineIds = [];
        for (const [idLinhaStr, entries] of byLine) {
          const idLinha = Number(idLinhaStr) || idLinhaStr;
          const people = entries.map((entry) => entry.item);
          const peoplePayload = people.map((p) => {
            const pos = p.position_por_linha && p.position_por_linha[String(idLinha)];
            let homeLat = Number(p.lat);
            let homeLng = Number(p.lng);
            if (!Number.isFinite(homeLat) || !Number.isFinite(homeLng)) {
              if (typeof this.resolvePassengerCoords === 'function') {
                const coords = this.resolvePassengerCoords(p, p);
                homeLat = Number(coords && coords.lat);
                homeLng = Number(coords && coords.lng);
              }
            }
            const routeLat = pos
              ? Number(pos.latitude != null ? pos.latitude : pos.lat)
              : homeLat;
            const routeLng = pos
              ? Number(pos.longitude != null ? pos.longitude : pos.lng)
              : homeLng;
            return {
              id_pessoa: p.id_pessoa,
              pcd: Number(p.pcd) === 1 || p.pcd === true ? 1 : 0,
              endereco: p.endereco,
              lat: homeLat,
              lng: homeLng,
              routeLat: Number.isFinite(routeLat) ? routeLat : homeLat,
              routeLng: Number.isFinite(routeLng) ? routeLng : homeLng,
            };
          });
          const result = await createFencesForAllocationStops({
            idLinha,
            people: peoplePayload,
            pollProcess: typeof this.pollProcess === 'function'
              ? (opts) => this.pollProcess(opts)
              : undefined,
            serviceTime: this.serviceTime,
            maxNumberofStops: this.maxNumberofStops,
            language: this.$i18n && this.$i18n.locale ? String(this.$i18n.locale) : 'pt-BR',
          });
          if (!result.ok) {
            const reasonKey = `crudLinhas.detail.allocation.createFenceError.${result.reason || 'persistFailed'}`;
            const detailed = this.$te(reasonKey) ? this.$t(reasonKey) : null;
            const message = result.message
              || detailed
              || this.$t('routePlanning.dialogs.allocatePeopleToLines.rerouteError');
            this.$emit('error', message);
            if (!this.publicationMode) {
              EventBus.$emit('show-alert', { tipo: 'error', mensagem: message });
            }
            return;
          }
          if (result.warning && !this.publicationMode) {
            EventBus.$emit('show-alert', { tipo: 'warning', mensagem: result.warning });
          }
          const fences = result.fencesByPessoa || {};
          for (const entry of entries) {
            const person = entry.item;
            const fence = fences[String(person.id_pessoa)];
            if (!fence || fence.id_cerca == null) continue;
            // A cerca criada é aplicada somente ao extremo variável do sentido solicitado.
            entry.fields.forEach((field) => {
              person[field] = fence.id_cerca;
            });
            this.syncTripStops(person);
          }
          delete this.fencesByLine[String(idLinha)];
          await this.ensureFencesForLines([idLinha]);
          if (result.routeReplaced) replacedLineIds.push(idLinha);
          this.$emit('cercas-changed', { idLinha, result });
          if (result.routeReplaced) {
            this.$emit('rotas-changed', { idLinha, result });
          }
        }
        await this.persistAllocation(allItems, {
          successMessage: this.$t('routePlanning.dialogs.allocatePeopleToLines.rerouteSuccess'),
          replacedLineIds,
        });
      } catch (error) {
        EventBus.$emit('show-error', { error });
        const message = this.$t('routePlanning.dialogs.allocatePeopleToLines.rerouteError');
        this.$emit('error', message);
        if (!this.publicationMode) {
          EventBus.$emit('show-alert', { tipo: 'error', mensagem: message });
        }
      } finally {
        this.rerouteLoading = false;
      }
    },
    async persistAllocation(sel, options = {}) {
      let allocationSaved = false;
      try {
        const byPessoa = new Map();
        (sel || []).forEach((item) => {
          if (item?.id_pessoa == null) return;
          byPessoa.set(String(item.id_pessoa), {
            id_pessoa: item.id_pessoa,
            id_grupo: item.id_grupo_alocado ?? null,
            id_linha_ida: item.id_linha_ida_alocada ?? null,
            id_linha_volta: item.id_linha_volta_alocada ?? null,
            id_cerca_embarque: item.id_cerca_embarque ?? null,
            id_cerca_desembarque: item.id_cerca_desembarque ?? null,
          });
        });
        const updates = Array.from(byPessoa.values());
        if (!updates.length) {
          const message = this.$t('routePlanning.publication.allocationSaveError');
          this.$emit('error', message);
          return;
        }
        this.$emit('saving');
        const response = await apiController.request(apiController.endpoints.alocaPessoasemlinhas, {
          data: {
            passageiros: updates,
            historico_sessao_id: this.historicoSessaoId || null,
            historico_sessao_origem_id: this.historicoSessaoOrigemId || null,
            historico_origem: this.historicoOrigem,
          },
          silent: true,
        });
        if (response.data && response.data.historicoAviso) {
          EventBus.$emit('show-alert', {
            tipo: 'warning',
            mensagem: this.$t('routePlanning.publication.allocationHistoryWarning'),
          });
        }
        if (response.data.erro) {
          const message = response.data.mensagem
            || this.$t('routePlanning.publication.allocationSaveError');
          this.$emit('error', message);
          if (!this.publicationMode) {
            EventBus.$emit('show-alert', { tipo: 'error', mensagem: message });
          }
        } else {
          const allocatedUpdates = updates.filter((u) => u
            && (u.id_linha_ida != null || u.id_linha_volta != null));
          const previouslyNoUnit = new Set(
            (sel || [])
              .filter((p) => p && passengerHasNoUnit(p) && p.id_pessoa != null)
              .map((p) => String(p.id_pessoa))
          );
          const successMessage = options.successMessage
            || response.data.mensagem
            || this.$t('routePlanning.publication.allocationSaved', {
              count: allocatedUpdates.length,
            });
          this.$emit('saved', {
            updates,
            allocatedUpdates,
            items: sel,
            response,
            previouslyNoUnit: [...previouslyNoUnit],
            successMessage,
            replacedLineIds: options.replacedLineIds || [],
            fencesByLine: { ...this.fencesByLine },
          });
          if (!this.publicationMode) {
            EventBus.$emit('show-alert', { tipo: 'success', mensagem: successMessage });
          }
          allocationSaved = true;
        }
      } catch (error) {
        EventBus.$emit('show-error', { error });
        this.$emit('error', this.$t('routePlanning.publication.allocationSaveError'));
      } finally {
        if (!this.publicationMode || allocationSaved) {
          this.close({ emitContinue: true });
        }
      }
    },
  },
};
</script>
